import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useApp } from '../contexts/AppContext';
import { useUserProfile } from '../hooks/useUserProfile';
import { AGENT_NAME_SHORT } from '../brand';

/**
 * SpendCapBanner — the question was not started because the organization is
 * at its monthly spending limit (HTTP 402, ``error: ALLOTMENT_EXCEEDED``).
 *
 * Arc is paused, not broken: nothing was spent on the blocked question, and
 * it resumes the moment the limit allows. Which limit it was decides what is
 * offered:
 *   - ``source: org_ceiling`` — the org's own ceiling. An org admin raises it
 *     right here and the question is re-sent; everyone else is pointed at an
 *     admin and can retry.
 *   - ``source: plan`` — the plan limit ChargeStack set. Nobody in the org
 *     can lift it; the banner says who can, and offers a retry.
 *
 * Props:
 *   detail       — the 402 body: { source, spent_client_usd,
 *                  allowance_client_usd, next_cycle_date }
 *   onAuthorized — () => void; re-send the blocked question
 *   onDismiss    — () => void
 */
export default function SpendCapBanner({ detail, onAuthorized, onDismiss }) {
  const { pricing } = useApp();
  const { profile } = useUserProfile();
  const isAdmin = profile?.role === 'admin';
  const ownCeiling = detail?.source === 'org_ceiling';
  const limit = Number(detail?.allowance_client_usd) || 0;
  const spent = Number(detail?.spent_client_usd) || 0;
  const showDollars = pricing?.show_costs !== false;

  // Suggest ~25% headroom over the current ceiling, rounded up to $10.
  const [amount, setAmount] = useState(() => (
    ownCeiling && limit > 0 ? String(Math.ceil((limit * 1.25) / 10) * 10) : ''
  ));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
  const resets = detail?.next_cycle_date
    ? new Date(`${detail.next_cycle_date}T00:00:00`).toLocaleDateString()
    : null;

  const authorize = async () => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n) || n <= 0) { setError('Enter an amount.'); return; }
    if (n <= spent) {
      setError(`The new ceiling must be above this month's spend (${fmt(spent)}).`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/org/settings', { ORG_MONTHLY_SPEND_CEILING_USD: n });
      const outcome = res?.results?.ORG_MONTHLY_SPEND_CEILING_USD;
      if (outcome?.status !== 'updated') throw new Error(outcome?.error || 'Could not update the ceiling');
      onAuthorized();
    } catch (err) {
      setError(err.message || 'Could not authorize');
      setBusy(false);
    }
  };

  return (
    <div className="alert alert-warning" role="status" aria-live="polite">
      <div>
        <strong>
          {ownCeiling ? 'Monthly spending ceiling reached' : 'Monthly plan limit reached'}
          {showDollars && limit > 0 && (ownCeiling ? ` — ${fmt(spent)} of ${fmt(limit)}` : ` (${fmt(limit)})`)}
          .
        </strong>
        <span style={{ marginLeft: 6 }}>
          {AGENT_NAME_SHORT} is paused and your question was not charged.
          {resets && ` The limit resets ${resets}.`}
        </span>
      </div>

      {!ownCeiling ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span>
            This limit is part of your organization’s ChargeStack plan. To raise it,
            contact your ChargeStack account manager.
          </span>
          <button type="button" className="btn btn-sm" onClick={onAuthorized}>Try again</button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onDismiss}>Dismiss</button>
        </div>
      ) : isAdmin ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <label htmlFor="spend-cap-amount">New monthly ceiling (USD)</label>
          <input
            id="spend-cap-amount"
            className="input"
            type="number"
            min="0"
            step="10"
            style={{ width: 120 }}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 100"
          />
          <button type="button" className="btn btn-sm btn-primary" onClick={authorize} disabled={busy || !amount}>
            {busy ? 'Authorizing…' : 'Authorize & continue'}
          </button>
          <Link to="/settings" className="btn btn-sm btn-ghost">Usage &amp; Billing</Link>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onDismiss} disabled={busy}>Not now</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span>An organization admin can authorize more under Settings → Usage &amp; Billing.</span>
          <button type="button" className="btn btn-sm" onClick={onAuthorized}>Try again</button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onDismiss}>Dismiss</button>
        </div>
      )}
      {error && <div style={{ marginTop: 6, color: '#b91c1c' }}>{error}</div>}
    </div>
  );
}
